import stylesheet from './main.scss';
import { type ToastPosition } from './types.ts';

/*
The Toaster acts as the controller for all Toasts by maintaining a queue of Toast data.
It also creates an overlay element and containers for Toasts to render into.

Each item in the toast queue is an object with:
    position - the position a toast wishes to occupy
    isVisible - whether or not to attempt to display the toast
    toastNode - the DOM node a Toast is rendered into via React Portal

If a QueuedToast has isVisible == true and its position is open, the QueuedToast's
"toastNode" is given the class "onStage". This class allows the Toast's enter
transition to start and begin the Toast lifecycle. When a Toast is "onStage", a flag for
the position it occupies is flipped preventing other Toasts from filling that position.

If a QueuedToast has isVisible == true and its position is filled, its "toastNode" does not
receive the "onStage" class and it remains rendered offscreen.

If a QueuedToast has isVisible == false it is rendered offscreen and remains offscreen until
the Toast calls popToast() and it is able to occupy the specified position.

When a Toast unmounts, it calls removeToast(), which removes the QueuedToast from the queue.
This allows another QueuedToast to occupy the now-vacant position.

If a Toast is closed via prop changes, it does not automatically unmount and thus
continues to occupy a position even though it is no longer visible.

When the viewport is tabletPortrait or smaller, the four positions are reduced
to "top" and "bottom".
 */

type GetDomNodeArgs = {
    position: ToastPosition;
    isVisible: boolean;
    zIndexClass: string;
    printHideClass?: string;
};

type QueuedToast = {
    position: string;
    isVisible: boolean;
    toastNode: HTMLElement;
};

// maintains the queue of toast data and controls when a toast is active
const toastQueue: Array<QueuedToast> = [];

export function updateToasterNodeClassname(className: string): void {
    const toasterNode = document.getElementById('dibs-toaster');
    if (toasterNode) {
        toasterNode.classList.add(className);
    }
}

export function updateToasterPosition(): void {
    const toasterNode = document.getElementById('dibs-toaster');
    if (!toasterNode) {
        return;
    }

    let verticalOffset = 0;
    const navbarBottomMarker = document.querySelector('#nav-bottom-marker');
    if (navbarBottomMarker) {
        // if the nav does not have a fixed position, it's bottom will become negative as the user scrolls down the page
        verticalOffset = Math.max(navbarBottomMarker.getBoundingClientRect().bottom, 0);
    }

    toasterNode.style.bottom = '0';
    toasterNode.style.top = `${verticalOffset}px`;
}

function getToasterNode(): HTMLElement {
    let toasterNode = document.getElementById('dibs-toaster');
    // if there is no dibs-toaster element in the DOM, render it
    // and add styling such that it overlays the entire page
    // with the exception of the nav bar
    if (!toasterNode) {
        toasterNode = document.createElement('div');
        toasterNode.id = 'dibs-toaster';
        toasterNode.className = stylesheet.toaster;
        if (document.body) {
            document.body.appendChild(toasterNode);
        }
    }
    return toasterNode;
}

function refreshQueue(): void {
    for (let i = 0; i < toastQueue.length; i++) {
        if (toastQueue[i].isVisible) {
            toastQueue[i].toastNode.className = stylesheet.onStage;
            break;
        }
    }
}

export function getDomNode({
    position,
    isVisible,
    zIndexClass,
    printHideClass,
}: GetDomNodeArgs): HTMLElement {
    const toasterNode = getToasterNode();
    updateToasterNodeClassname(zIndexClass);
    if (printHideClass) {
        updateToasterNodeClassname(printHideClass);
    }
    updateToasterPosition();
    const toastNode = document.createElement('div');
    toasterNode.appendChild(toastNode);
    toastQueue.push({ position, isVisible, toastNode });
    return toastNode;
}

export function removeToast(toastNode?: HTMLElement | null): void {
    if (!toastNode) {
        return;
    }
    const index = toastQueue.findIndex(toast =>
        toastNode ? toast.toastNode.isSameNode(toastNode) : false
    );
    if (index !== -1) {
        toastQueue[index].toastNode.remove();
        toastQueue.splice(index, 1);
        refreshQueue();
    }
}

export function popToast(toastNode?: HTMLElement | null): void {
    if (!toastNode) {
        return;
    }
    const toastToOpen = toastQueue.find(toast =>
        toastNode ? toast.toastNode.isSameNode(toastNode) : false
    );
    if (toastToOpen) {
        toastToOpen.isVisible = true;
        refreshQueue();
    }
}
